<template>
  <div 
    v-if="visibleBadges.length > 0"
    :class="[positionClasses, 'absolute z-10 flex flex-col gap-2']"
  >
    <div
      v-for="badge in visibleBadges"
      :key="badge.label"
      class="flex items-center justify-center shadow-lg py-1 px-3 text-xs rounded font-bold"
      :style="`background-color: ${badge.color}; color: ${badge.text_color}`"
    >
      {{ t(`products.badge.${badge.label.toLowerCase()}`) }}
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  product?: any;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  maxBadges?: number;
}

const props = withDefaults(defineProps<Props>(), {
  position: 'bottom-left',
  maxBadges: 2,
});

const { t } = useI18n();

// Position CSS classes mapping
const positionClasses = computed(() => {
  const positions = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2', 
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2',
  };
  return positions[props.position];
});

// Badge priority scores (higher = more important)
const BADGE_PRIORITIES = {
  black_friday: 100,
  promo: 90,
  outlet: 80,
  offer: 70,
  sale: 70,
  discount: 70,
  new_product: 60,
  limited: 50,
} as const;

const getBadgePriority = (label: string): number => {
  return BADGE_PRIORITIES[label.toLowerCase() as keyof typeof BADGE_PRIORITIES] || 10;
};

// Business rules for badge combinations
const applyBusinessRules = (badges: any[]) => {
  if (badges.length <= 1) return badges;

  const hasOutlet = badges.some(b => b.label.toLowerCase() === 'outlet');
  const hasNew = badges.some(b => b.label.toLowerCase() === 'new_product');
  const hasPromo = badges.some(b => b.label.toLowerCase() === 'promo');
  const hasOffer = badges.some(b => ['offer', 'sale', 'discount'].includes(b.label.toLowerCase()));

  // Rule 1: Outlet + New = Never together (keep outlet, exclude new)
  if (hasOutlet && hasNew) {
    return badges.filter(b => b.label.toLowerCase() !== 'new_product');
  }

  // Rule 4: Offers + Promos = Never together (keep promo, exclude offers)
  if (hasPromo && hasOffer) {
    return badges.filter(b => !['offer', 'sale', 'discount'].includes(b.label.toLowerCase()));
  }

  return badges;
};

// Main logic: get visible badges respecting all rules
const visibleBadges = computed(() => {
  const badges = props.product?.badges || [];
  if (badges.length === 0) return [];

  // Sort by priority (highest first)
  const sortedBadges = [...badges].sort((a, b) => 
    getBadgePriority(b.label) - getBadgePriority(a.label)
  );

  // Apply business rules to filter incompatible combinations
  const filteredBadges = applyBusinessRules(sortedBadges);

  // Return up to maxBadges
  return filteredBadges.slice(0, props.maxBadges);
});
</script>